import { useRef } from 'react';

export interface SelectionRange {
  start: number;
  end: number;
}

export default <T extends HTMLInputElement | HTMLTextAreaElement>() => {
  const selectionRef = useRef<SelectionRange | null>(null);

  const captureFromTarget = (target: T | null) => {
    if (!target || typeof target.selectionStart !== 'number') {
      selectionRef.current = null;
      return;
    }

    selectionRef.current = {
      start: target.selectionStart,
      end:
        typeof target.selectionEnd === 'number'
          ? target.selectionEnd
          : target.selectionStart,
    };
  };

  const captureFromEvent = (event: { target: T }) => {
    captureFromTarget(event.target);
  };

  const restoreSelection = (element: T | null) => {
    const selection = selectionRef.current;
    if (!element || !selection) return;

    const length = element.value.length;
    const start = Math.min(selection.start, length);
    const end = Math.min(selection.end, length);

    try {
      element.selectionStart = start;
      element.selectionEnd = end;
    } catch {
      // ignore invalid selection positions
    }
  };

  const restoreOnNextTick = (getElement: () => T | null) => {
    setTimeout(() => {
      const element = getElement();
      if (!element) return;

      restoreSelection(element);
    }, 0);
  };

  return {
    selectionRef,
    captureFromTarget,
    captureFromEvent,
    restoreSelection,
    restoreOnNextTick,
  };
};
